/**
 * flexible_frame schema shared with mobile (3×3 regions, ratios in cell / image space).
 */

export type FlexibleFrameRatios = {
  left_ratio: number;
  right_ratio: number;
  top_ratio: number;
  bottom_ratio: number;
  scale_ratio: number;
  /** width / height of the axis-aligned bbox used when encoding */
  aspect_ratio: number;
  region: number;
  center_x_ratio: number;
  center_y_ratio: number;
};

/** Optional ios_* fields used in-memory before API strip (web + admin save paths). */
export type FlexibleFrameWithIos = FlexibleFrameRatios & {
  ios_height?: number;
  ios_width?: number;
};

export type ImageFrame = { width: number; height: number };

export type WatermarkRect = { x: number; y: number; width: number; height: number };

/**
 * Encode flexible_frame from an axis-aligned bbox (e.g. rotated watermark AABB) in image space.
 * Matches web `getFlexibleEdgedFrameValue`: 3×3 cell ratios + edge pixel tolerance on encode.
 */
export function getFlexibleEdgedFrameValue(
  watermarkFrame: WatermarkRect,
  imageFrame: ImageFrame
): FlexibleFrameRatios {
  const imageW = imageFrame.width;
  const imageH = imageFrame.height;
  const regionW = imageW / 3;
  const regionH = imageH / 3;

  const wmLeft = watermarkFrame.x;
  const wmTop = watermarkFrame.y;
  const wmRight = wmLeft + watermarkFrame.width;
  const wmBottom = wmTop + watermarkFrame.height;

  const watermarkCenterX = wmLeft + watermarkFrame.width / 2;
  const watermarkCenterY = wmTop + watermarkFrame.height / 2;

  const col = Math.min(Math.floor(watermarkCenterX / regionW), 2);
  const row = Math.min(Math.floor(watermarkCenterY / regionH), 2);
  const region = row * 3 + col;

  const regionLeft = col * regionW;
  const regionTop = row * regionH;
  const regionRight = regionLeft + regionW;
  const regionBottom = regionTop + regionH;
  const ref = Math.min(regionW, regionH);

  const edgePxTol = 6;
  const edgeRatio = (gapPx: number, axisPx: number) => {
    if (axisPx <= 0) return 0;
    const g = Number(gapPx) || 0;
    if (Math.abs(g) <= edgePxTol) return 0;
    return g / axisPx;
  };

  return {
    left_ratio: edgeRatio(wmLeft - regionLeft, regionW),
    right_ratio: edgeRatio(regionRight - wmRight, regionW),
    top_ratio: edgeRatio(wmTop - regionTop, regionH),
    bottom_ratio: edgeRatio(regionBottom - wmBottom, regionH),
    scale_ratio: watermarkFrame.width / ref,
    aspect_ratio: watermarkFrame.width / Math.max(0.0001, watermarkFrame.height),
    region,
    center_x_ratio: watermarkCenterX / imageW,
    center_y_ratio: watermarkCenterY / imageH,
  };
}

export type FlexibleFrameDecodeInput = {
  left_ratio: number;
  right_ratio: number;
  top_ratio: number;
  bottom_ratio: number;
  scale_ratio: number;
  aspect_ratio: number;
  region?: number;
  center_x_ratio: number;
  center_y_ratio: number;
};

/**
 * Decode flexible_frame to axis-aligned bbox in image space.
 * Matches web `getEdgedFrameValueFromFlexibleFrame` / mobile apply logic.
 */
export function getEdgedFrameValueFromFlexibleFrame(
  flexibleFrame: FlexibleFrameDecodeInput | null | undefined,
  imageSize: ImageFrame | null | undefined
): WatermarkRect | null {
  if (!flexibleFrame || !imageSize) {
    return null;
  }

  const imageW = imageSize.width;
  const imageH = imageSize.height;

  const regionW = imageW / 3;
  const regionH = imageH / 3;

  const rawRegion = Number(flexibleFrame.region);
  const region = Number.isFinite(rawRegion) ? Math.max(0, Math.min(8, Math.floor(rawRegion))) : 4;

  const col = region % 3;
  const row = Math.floor(region / 3);

  const regionLeft = col * regionW;
  const regionTop = row * regionH;
  const ref = Math.min(regionW, regionH);

  const scaleRatio = Number(flexibleFrame.scale_ratio) || 0;
  const ar = Math.max(0.0001, Number(flexibleFrame.aspect_ratio) || 1);
  const width = scaleRatio > 0 ? scaleRatio * ref : 0;
  const height = width > 0 ? width / ar : 0;

  let x = 0;
  let y = 0;
  switch (region) {
    case 0:
      x = regionLeft + flexibleFrame.left_ratio * regionW;
      y = regionTop + flexibleFrame.top_ratio * regionH;
      break;
    case 1:
      x = flexibleFrame.center_x_ratio * imageW - width / 2;
      y = regionTop + flexibleFrame.top_ratio * regionH;
      break;
    case 2:
      x = regionLeft + regionW - flexibleFrame.right_ratio * regionW - width;
      y = regionTop + flexibleFrame.top_ratio * regionH;
      break;
    case 3:
      x = regionLeft + flexibleFrame.left_ratio * regionW;
      y = flexibleFrame.center_y_ratio * imageH - height / 2;
      break;
    case 4:
      x = flexibleFrame.center_x_ratio * imageW - width / 2;
      y = flexibleFrame.center_y_ratio * imageH - height / 2;
      break;
    case 5:
      x = regionLeft + regionW - flexibleFrame.right_ratio * regionW - width;
      y = flexibleFrame.center_y_ratio * imageH - height / 2;
      break;
    case 6:
      x = regionLeft + flexibleFrame.left_ratio * regionW;
      y = regionTop + regionH - flexibleFrame.bottom_ratio * regionH - height;
      break;
    case 7:
      x = flexibleFrame.center_x_ratio * imageW - width / 2;
      y = regionTop + regionH - flexibleFrame.bottom_ratio * regionH - height;
      break;
    default:
      x = regionLeft + regionW - flexibleFrame.right_ratio * regionW - width;
      y = regionTop + regionH - flexibleFrame.bottom_ratio * regionH - height;
      break;
  }

  return {
    x,
    y,
    width,
    height,
  };
}
